/**
 * JOY Toolkit — gated results reveal
 *
 * The Gravity Forms confirmation renders only an empty container carrying an
 * entry id and a one-time token. The resources themselves are fetched here, so
 * they are never in the page source before the form is completed.
 *
 * Gravity Forms replaces the form via AJAX, so the container can appear long
 * after DOMContentLoaded — hence the MutationObserver as well as the initial
 * sweep.
 */

const ENDPOINT_ATTR = "data-joy-results";
const loaded = new WeakSet();

/**
 * Announce and render an error in place of the results.
 * @param {Element} root
 * @param {string} message
 */
function showError(root, message) {
  const status = root.querySelector(".joy-results__status");
  if (!status) return;
  status.classList.add("joy-results__status--error");
  status.textContent = message;
}

/**
 * Move focus to the results so keyboard and screen reader users land on the
 * new content rather than being left where the form used to be.
 * @param {Element} root
 */
function focusResults(root) {
  const heading = root.querySelector("h3");
  const target = heading || root;

  if (!target.hasAttribute("tabindex")) {
    target.setAttribute("tabindex", "-1");
  }

  target.focus({ preventScroll: true });

  const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  target.scrollIntoView({
    behavior: reduced ? "auto" : "smooth",
    block: "start",
  });
}

/**
 * Fetch and render the toolkit for one container.
 * @param {Element} root
 */
async function loadResults(root) {
  if (loaded.has(root)) return;
  loaded.add(root);

  const endpoint = root.dataset.endpoint;
  const entry = root.dataset.entry;
  const token = root.dataset.token;

  if (!endpoint || !entry || !token) {
    showError(root, "We could not load your resources. Please contact us.");
    return;
  }

  const body = new FormData();
  body.append("action", "joy_toolkit_results");
  body.append("entry", entry);
  body.append("token", token);

  try {
    const response = await fetch(endpoint, {
      method: "POST",
      body,
      credentials: "same-origin",
    });

    const payload = await response.json();

    if (!payload || !payload.success || !payload.data || !payload.data.html) {
      const message =
        (payload && payload.data && payload.data.message) ||
        "We could not load your resources. Please contact us.";
      showError(root, message);
      return;
    }

    root.innerHTML = payload.data.html;
    root.setAttribute("data-loaded", "true");

    // The form block's heading ("Tell us a little about you") prompts the
    // FORM. Once the form has been replaced by results it is stale, and
    // competes with the page's own hero heading. Flag the block so CSS can
    // retire it — reversible, and leaves the heading intact for the form.
    const block = root.closest(".form-block");
    if (block) block.classList.add("form-block--has-results");

    // The token is single-use from the page's point of view; don't leave it
    // sitting in the DOM once it has been redeemed.
    delete root.dataset.token;

    focusResults(root);
  } catch (error) {
    showError(
      root,
      "We could not load your resources. Please check your connection and try again.",
    );
  }
}

/**
 * Load any containers currently in the document.
 * @param {ParentNode} scope
 */
function scan(scope) {
  scope.querySelectorAll(`[${ENDPOINT_ATTR}]`).forEach(loadResults);
}

function init() {
  scan(document);

  // Gravity Forms swaps the form for its confirmation after this script runs.
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== 1) continue;
        if (node.hasAttribute && node.hasAttribute(ENDPOINT_ATTR)) {
          loadResults(node);
        } else if (node.querySelectorAll) {
          scan(node);
        }
      }
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init, { once: true });
} else {
  init();
}
